import { AvLog } from "../models/avLog";
import { ServerTime } from "../models/server-time";
import { addParamArrToUrl, API } from "./api-services";
import { convertUtmCookiesToParams, getUTMCookies } from "./cookie-service";
import { encryptAES } from "./encrypt.service";
import VMAP from "@dailymotion/vmap";
import { VASTParser } from "@dailymotion/vast-client";

const BITRATE_1080P = 4000;
const BITRATE_720P = 1400;
const BITRATE_480P = 662;
const BITRATE_240P = 357;
const BITRATE_160P = 153;

interface CampaignLogModel {
  level2_id: number;
  uid: string;
  watch_time: number;
}

export const postAvLog = async (
  auditLogString: string,
  dg_id: string,
  token?: string
) => {
  let mutableAuditLog: AvLog;
  let serverTime: ServerTime;
  try {
    mutableAuditLog = JSON.parse(auditLogString);
  } catch (error) {
    console.log("could not parse existing auditLog");
    return;
  }
  const fileId = mutableAuditLog?.log_details?.av_file_data?.id;
  if (isNaN(fileId)) {
    //console.log("auditLog's FileId is not number");
    return;
  }

  let url = addParamArrToUrl(API.request_AvFileLog(fileId), [
    { param: "platform", value: "web" },
    { param: "dg_id", value: dg_id }
  ]);

  const utmCookies = getUTMCookies();
  url = utmCookies
    ? addParamArrToUrl(url, convertUtmCookiesToParams(utmCookies))
    : url;

  try {
    const serverTimeResponse = await API.fetch(API.request_server_time);
    serverTime = await serverTimeResponse.json();
  } catch (error) {
    console.log("unable to get server time; ", error);
    return;
  }

  try {
    mutableAuditLog.additional_data.created_time = serverTime.server_time;
    if (token) {
      await API.fetchAuth({
        url,
        token,
        requestInfo: { method: "POST", body: JSON.stringify(mutableAuditLog) }
      });
    } else {
      const rawData = `{"t":"${serverTime.server_time}","f":${fileId}}`;
      const encData = await encryptAES(rawData);
      mutableAuditLog.enc_data = encData;
      await API.fetch(url, {
        method: "POST",
        body: JSON.stringify(mutableAuditLog)
      });
    }
  } catch (error) {
    console.log("could not send avLog");
    return;
  } finally {
    localStorage.removeItem("avLog");
  }
};

export const postCampaignLog = async (
  data: CampaignLogModel,
  token?: string
) => {
  try {
    if (token) {
      await API.fetchAuth({
        url: API.post_campaign,
        token,
        requestInfo: { method: "POST", body: JSON.stringify(data) }
      });
    } else {
      await API.fetch(API.post_campaign, {
        method: "POST",
        body: JSON.stringify(data)
      });
    }
  } catch (error) {
    console.log("could not send campaignLog");
    return;
  }
};

export const detectQualityLable = (bitrate: number) => {
  let quality;
  switch (true) {
    case bitrate > 0 && bitrate <= BITRATE_160P: {
      quality = `۱۶۰P  کیفیت ضعیف`;
      break;
    }
    case bitrate >= BITRATE_160P && bitrate <= BITRATE_240P: {
      quality = `۲۴۰P کیفیت متوسط`;
      break;
    }
    case bitrate >= BITRATE_240P && bitrate <= BITRATE_480P: {
      quality = `۴۸۰P کیفیت معمولی`;
      break;
    }
    case bitrate >= BITRATE_480P && bitrate <= BITRATE_720P: {
      quality = `۷۲۰P کیفیت خوب`;
      break;
    }
    case bitrate >= BITRATE_720P && bitrate <= BITRATE_1080P: {
      quality = `۱۰۸۰P کیفیت عالی`;
      break;
    }
    case bitrate >= BITRATE_1080P: {
      quality = `۴K بالاترین کیفیت`;
      break;
    }
    case bitrate === 0: {
      quality = "کیفیت خودکار";
      break;
    }
    default:
      quality = "کیفیت نامشخص ";
  }
  return quality;
};

export const getAds = async (url: string) => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      return {
        status: response.status,
        message: `Network response was not ok. Status code: ${response.status}`,
        ok: response.ok
      };
    }

    const responseText = await response.text();

    const vmap = new VMAP(
      new DOMParser().parseFromString(responseText, "application/xml")
    );
    return { data: vmap, status: response.status, ok: response.ok };
  } catch (error) {
    console.error("Error:", error);
    //there is a custom status error
    return { message: error.message, status: 1000 };
  }
};

export const sendStartEventLog = async (vmap: any, params: Object) => {
  try {
    let campaign_id = 0;
    let campaign_title = "";
    const vast = await getVastContent(vmap);

    if (vast?.ads && vast?.ads.length > 0) {
      campaign_id = vast.ads[0]?.id;
      campaign_title = vast.ads[0]?.title;
    }
    const queryParams = {
      ...params,
      event_type: "start",
      campaign_title: campaign_title,
      campaign_id: campaign_id.toString()
    };

    //@ts-ignore
    const queryString = new URLSearchParams(queryParams);
    const apiUrlWithParams = `${API.request_event_campaign}?${queryString}`;
    API.fetch(apiUrlWithParams);
  } catch (error) {
    console.error("API request error:", error.message);
    return;
  }
};

const getVastContent = (vmap: any) => {
  const xml = vmap.adBreaks[0].adSource.vastAdData.outerHTML as string;

  if (xml) {
    const vastParser = new VASTParser();

    let xmlDocument;

    if (xml.constructor === window.XMLDocument) {
      xmlDocument = xml;
    } else if (xml.constructor === String) {
      xmlDocument = new window.DOMParser().parseFromString(xml, "text/xml");
    } else {
      throw new Error("xml config option must be a String or XMLDocument");
    }

    return vastParser.parseVAST(xmlDocument);
  }
  return Promise.reject(new Error("xml option not provided"));
};

export const assignStreamLinkParams = (url: string, config: {
  campaign?: string;
  user_id?: string;
  trace_id?: string;
  source?: string;
  platform?: string;
}) => {
  let _url = url;
  for (let key in config) {
    _url = _url.replace(key+"=null", key+"="+config[key]);
  }

  return _url;
} 
